import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Export, DownloadSimple, Trash, PaperPlaneTilt } from '@phosphor-icons/react';

export default function ExportsPage() {
  const { t, i18n } = useTranslation();
  const [exports, setExports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showExport, setShowExport] = useState(false);
  const [exportForm, setExportForm] = useState({
    name: 'Export',
    filters: { status: '' },
    include_generic_emails: false,
  });
  const [exporting, setExporting] = useState(false);
  const [exportToDelete, setExportToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    api.get('/exports').then(r => { setExports(r.data.items); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const runExport = async () => {
    setExporting(true);
    try {
      const filters = {};
      if (exportForm.filters.status) filters.status = exportForm.filters.status;
      const { data } = await api.post('/exports/csv', {
        name: exportForm.name,
        filters,
        export_type: 'csv',
        destination: 'download',
        include_generic_emails: !!exportForm.include_generic_emails,
      });
      toast.success(t('workspace.exports.created', { count: data.record_count }));
      // Download CSV
      if (data.csv_content && data.csv_content !== 'No matching records found') {
        const blob = new Blob([data.csv_content], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${exportForm.name}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
      setShowExport(false);
      const refreshed = await api.get('/exports');
      setExports(refreshed.data.items);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setExporting(false); }
  };

  const confirmDeleteExport = async () => {
    if (!exportToDelete || deleting) return;
    const targetId = exportToDelete.id;
    setDeleting(true);
    try {
      await api.delete(`/exports/${targetId}`);
      setExports((prev) => prev.filter((item) => item.id !== targetId));
      setExportToDelete(null);
      toast.success(t('workspace.exports.deleted'));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div data-testid="exports-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tighter text-gray-900">{t('workspace.exports.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {t('workspace.exports.subtitle')}{' '}
            <Link to="/app/review-contacts" className="underline text-gray-800 hover:text-black" data-testid="exports-to-review-link">
              {t('nav.reviewContacts')}
            </Link>
            .
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild variant="outline" data-testid="go-review-contacts-btn">
            <Link to="/app/review-contacts">{t('nav.reviewContacts')}</Link>
          </Button>
          <Button asChild variant="outline" data-testid="export-push-pro-email-btn">
            <Link to="/app/review-contacts?action=push">
              <PaperPlaneTilt size={16} className="mr-2" /> {t('workspace.companies.push')}
            </Link>
          </Button>
          <Button onClick={() => setShowExport(true)} className="bg-black text-white hover:bg-gray-800" data-testid="new-export-btn">
            <Export size={16} className="mr-2" /> {t('workspace.exports.new')}
          </Button>
        </div>
      </div>

      <div className="border border-gray-200 rounded-md bg-white overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              {['name', 'records', 'status', 'date', 'actions'].map((key) => <th key={key} className="text-xs font-medium text-gray-500 uppercase tracking-wider py-3 px-4 text-left">{t(`workspace.exports.${key}`)}</th>)}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="5" className="py-12 text-center text-gray-400">{t('common.loading')}</td></tr>
            ) : exports.length === 0 ? (
              <tr><td colSpan="5" className="py-12 text-center text-gray-400">{t('workspace.exports.empty')}</td></tr>
            ) : exports.map(e => (
              <tr key={e.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`export-row-${e.id}`}>
                <td className="py-3 px-4 text-sm text-gray-900">{e.name}</td>
                <td className="py-3 px-4 font-mono text-sm text-gray-700">{e.record_count}</td>
                <td className="py-3 px-4"><span className="inline-flex px-2 py-0.5 rounded-full text-xs font-mono font-medium border bg-emerald-50 text-emerald-700 border-emerald-200">{e.status}</span></td>
                <td className="py-3 px-4 text-xs font-mono text-gray-500">{e.created_at ? new Date(e.created_at).toLocaleDateString(i18n.resolvedLanguage) : '-'}</td>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          const { data } = await api.get(`/exports/${e.id}`);
                          if (data.csv_content) {
                            const blob = new Blob([data.csv_content], { type: 'text/csv' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a'); a.href = url; a.download = `${e.name}.csv`; a.click(); URL.revokeObjectURL(url);
                          }
                        } catch (err) { toast.error(t('workspace.exports.downloadFailed')); }
                      }}
                      className="text-gray-500 hover:text-gray-900"
                      aria-label={`${t('workspace.exports.download')} ${e.name}`}
                      title={t('workspace.exports.download')}
                      data-testid={`download-export-${e.id}`}
                    >
                      <DownloadSimple size={18} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setExportToDelete(e)}
                      className="text-gray-400 hover:text-red-600"
                      aria-label={`${t('workspace.exports.delete')} ${e.name}`}
                      title={t('workspace.exports.delete')}
                      data-testid={`delete-export-${e.id}`}
                    >
                      <Trash size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={showExport} onOpenChange={setShowExport}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">{t('workspace.exports.dialogTitle')}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div><label className="text-xs font-mono uppercase text-gray-500 mb-1 block">{t('workspace.exports.exportName')}</label>
              <Input value={exportForm.name} onChange={e => setExportForm(p => ({...p, name: e.target.value}))} data-testid="export-name-input" /></div>
            <div><label className="text-xs font-mono uppercase text-gray-500 mb-1 block">{t('workspace.exports.filterStatus')}</label>
              <Select value={exportForm.filters.status} onValueChange={v => setExportForm(p => ({...p, filters: {...p.filters, status: v === 'all' ? '' : v}}))}>
                <SelectTrigger className="bg-white border-gray-300"><SelectValue placeholder={t('workspace.exports.allStatuses')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('workspace.exports.all')}</SelectItem>
                  <SelectItem value="approved">{t('workspace.exports.approvedOnly')}</SelectItem>
                  <SelectItem value="new">{t('workspace.exports.newOnly')}</SelectItem>
                  <SelectItem value="needs_review">{t('workspace.exports.review')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-2">
              <p className="text-xs font-mono uppercase text-gray-500">Contact mode</p>
              <p className="text-sm text-gray-700" data-testid="export-mode-default-label">
                Personal contacts only (default) — one CSV row per decision-maker.
              </p>
              <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={!!exportForm.include_generic_emails}
                  onChange={(e) => setExportForm((p) => ({ ...p, include_generic_emails: e.target.checked }))}
                  data-testid="export-include-generic-emails"
                />
                <span>
                  Include generic emails (<span className="font-mono text-xs">info@</span>,{' '}
                  <span className="font-mono text-xs">sales@</span>, …).
                  When unchecked, generics are used only if a company has no personal contacts.
                </span>
              </label>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowExport(false)}>{t('common.cancel')}</Button>
              <Button onClick={runExport} disabled={exporting} className="bg-black text-white hover:bg-gray-800" data-testid="run-export-btn">{exporting ? t('workspace.exports.exporting') : t('workspace.exports.exportCsv')}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!exportToDelete}
        onOpenChange={(open) => {
          if (!open && !deleting) setExportToDelete(null);
        }}
      >
        <DialogContent className="sm:max-w-md" data-testid="delete-export-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t('workspace.exports.deleteTitle')}</DialogTitle>
            <DialogDescription className="text-sm text-gray-600 pt-2 space-y-2">
              <span className="block">
                <span className="sr-only" data-testid="delete-export-name">{exportToDelete?.name}</span>
                {t('workspace.exports.deleteQuestion', { name: exportToDelete?.name, count: exportToDelete?.record_count ?? 0 })}
              </span>
              <span className="block">
                {t('workspace.exports.deleteWarning')}
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4 gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setExportToDelete(null)}
              disabled={deleting}
              data-testid="cancel-delete-export-btn"
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteExport}
              disabled={deleting}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="confirm-delete-export-btn"
            >
              {deleting ? t('workspace.exports.deleting') : t('workspace.exports.deleteButton')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
